/**
 * AddReceiptCxP.js — Formulario pago a proveedor
 * UI idéntico a AddReceipt: receipt-container (1fr 1fr), receipt-details (izq),
 * cxc-details (der) ahora = "Facturas Pendientes", receipt-footer (bottom).
 * CSS: addReceipt.module.css (mismas clases que AddReceipt).
 */
import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import apiFetch from "../../../../shared/services/apiClient";
import styles from "../../receipts/styles/addReceipt.module.css";
import { formatCurrency } from "../../../../shared/utils/formatCurrency";
import { MonedaSelector } from "../../components/MonedaSelector";
import { useTipoCambio } from "../../services/useTipoCambio";
import { useMultimoneda } from "../../../../shared/hooks/useMultimoneda";

const r2  = (n) => Math.round(Number(n) * 100) / 100;
const fmt = (n) => Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2 });

function FieldError({ msg }) {
  if (!msg) return null;
  return (
    <div style={{ display:"flex", alignItems:"center", gap:5,
      color:"#f87171", fontSize:"0.77rem", marginTop:4, fontWeight:500 }}>
      <i className="fa-solid fa-circle-exclamation" style={{ fontSize:"0.72rem" }}/>
      {msg}
    </div>
  );
}

const PAGE_SIZE = 10;

/**
 * Facturas del proveedor con saldo distinto de 0 (incluye saldos a favor,
 * saldo < 0), ordenadas con los saldos a favor primero.
 */
function pickFacturas(all, proveedorId) {
  return all
    .filter((cxp) =>
      cxp.factura_cxp?.cuenta_proveedor?.id === proveedorId &&
      r2(Number(cxp.saldo)) !== 0)
    .sort((a, b) => {
      const na = Number(a.saldo) < 0, nb = Number(b.saldo) < 0;
      if (na && !nb) return -1;
      if (!na && nb) return 1;
      return 0;
    });
}

/** Valor en colones de un saldo (negativo para saldos a favor). */
function valorCRC(f) {
  if ((f.moneda || "CRC") === "USD") {
    return Number(f.saldo) < 0
      ? Number(f.saldo) * (Number(f.tipo_cambio) || 1)   // crédito USD
      : Number(f.saldo_colones || 0);
  }
  return Number(f.saldo);
}

export default function AddReceiptCxP() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // ── Form state ────────────────────────────────────────────────────────────
  const [form, setForm] = useState({
    fecha_pago:      new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().split("T")[0],
    monto:           "",
    metodo_pago:     "TRANSFERENCIA",
    documento:       "",
    descripcion:     "",
    cuenta_bancaria: null,
    cuenta_proveedor: null,
    cuenta_proveedor_nombre: "",
    moneda_pago:     "CRC",
    tipo_cambio:     1,
    tratar_como_diferencial: false,
  });
  const { tipoCambio, loading: loadingTC } = useTipoCambio();
  const multimoneda = useMultimoneda();

  // Auto-populate TC from BCCR (tipo_compra) — solo cuando el TC es 1 (valor inicial)
  useEffect(() => {
    if (tipoCambio?.tipo_compra && (!form.tipo_cambio || form.tipo_cambio === 1)) {
      setForm(p => ({ ...p, tipo_cambio: Number(tipoCambio.tipo_compra) }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipoCambio, form.tipo_cambio]);

  // ── Data ──────────────────────────────────────────────────────────────────
  const [cuentasBancarias, setCuentasBancarias] = useState([]);
  const [cuentasProveedor, setCuentasProveedor] = useState([]);
  const [facturasCxP,      setFacturasCxP]      = useState([]);
  const [selectedFacturas, setSelectedFacturas] = useState(new Set());

  // ── UI ────────────────────────────────────────────────────────────────────
  const [errors,     setErrors]     = useState({});
  const [saving,     setSaving]     = useState(false);
  const [apiErr,     setApiErr]     = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [loadingFac, setLoadingFac] = useState(false);
  const [page,       setPage]       = useState(1);

  // ── Load banks and proveedores ─────────────────────────────────────────────
  useEffect(() => {
    apiFetch("/api/finance/cuentaBancaria/")
      .then(r => r.json())
      .then(d => {
        const list = d.results ?? d ?? [];
        setCuentasBancarias(list);
        if (list.length > 0) {
          const first = list[0];
          setForm(p => ({
            ...p,
            cuenta_bancaria: first.id,
            moneda_pago: (first.moneda || 'CRC').toUpperCase(),
          }));
        }
      });
    apiFetch("/api/finance/catalogo/detalle/?tipo=PASIVO&excluir_a_favor=1&page_size=500")
      .then(r => r.json())
      .then(d => setCuentasProveedor(Array.isArray(d) ? d : (d.results ?? [])));
  }, []);

  // ── Load facturas when proveedor changes ───────────────────────────────────
  useEffect(() => {
    if (!form.cuenta_proveedor) { setFacturasCxP([]); setSelectedFacturas(new Set()); return; }
    setLoadingFac(true);
    apiFetch(`/api/finance/cuentaXPagar/?page_size=9999`)
      .then(r => r.json())
      .then(d => {
        const all = d.results ?? [];
        setFacturasCxP(pickFacturas(all, form.cuenta_proveedor));
        setSelectedFacturas(new Set());
        setPage(1);
      })
      .finally(() => setLoadingFac(false));
  }, [form.cuenta_proveedor]);

  // Auto-set monto cuando cambian las facturas seleccionadas
  // Si todas son en la misma moneda que el pago → sumar saldos directamente.
  // Si hay facturas en otra moneda → sumar saldo_colones (equivalente en ₡).
  useEffect(() => {
    if (selectedFacturas.size === 0) return;
    const sel = facturasCxP.filter(f => selectedFacturas.has(f.id));
    const todasMismaMoneda = sel.every(
      f => (f.moneda || 'CRC') === form.moneda_pago
    );
    let total;
    if (todasMismaMoneda) {
      total = sel.reduce((s, f) => s + Number(f.saldo), 0);
    } else if (form.moneda_pago === 'CRC') {
      // Pago en CRC, alguna factura en USD → usar saldo_colones
      total = sel.reduce((s, f) => {
        if ((f.moneda || 'CRC') === 'CRC') return s + Number(f.saldo);
        return s + Number(f.saldo_colones || 0);
      }, 0);
    } else {
      // Pago en USD, facturas en CRC → dividir saldo entre TC
      const tc = Number(form.tipo_cambio) || 1;
      total = sel.reduce((s, f) => {
        if ((f.moneda || 'CRC') === 'USD') return s + Number(f.saldo);
        return s + Number(f.saldo_colones || f.saldo) / tc;
      }, 0);
    }
    setForm(p => ({ ...p, monto: String(r2(total)) }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFacturas, facturasCxP, form.moneda_pago, form.tipo_cambio]);

  function set(field, val) { setForm(p => ({ ...p, [field]: val })); }

  function handleProveedor(e) {
    const id  = Number(e.target.value) || null;
    const obj = cuentasProveedor.find(c => c.id === id);
    setForm(p => ({ ...p, cuenta_proveedor: id, cuenta_proveedor_nombre: obj?.nombre ?? "" }));
  }

  function handleBancoChange(e) {
    const id    = Number(e.target.value) || null;
    const banco = cuentasBancarias.find(b => b.id === id);
    const moneda = (banco?.moneda || 'CRC').toUpperCase();
    setForm(p => ({ ...p, cuenta_bancaria: id, moneda_pago: moneda }));
  }

  function toggleFactura(id) {
    setSelectedFacturas(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }

  function toggleAll() {
    setSelectedFacturas(
      selectedFacturas.size === facturasCxP.length
        ? new Set()
        : new Set(facturasCxP.map(f => f.id))
    );
  }

  function validate() {
    const e = {};
    const sel    = facturasCxP.filter(f => selectedFacturas.has(f.id));
    const hayNeg = sel.some(f => Number(f.saldo) < 0);
    const hayPos = sel.some(f => Number(f.saldo) > 0);
    if (!form.cuenta_proveedor)                  e.cuenta_proveedor = t("cxp.proveedorReqMsg");
    if (!form.fecha_pago)                        e.fecha_pago       = t("cxp.fechaPagoReq");
    if (!form.monto || Number(form.monto) <= 0)  e.monto            = hayNeg
      ? t("cxp.creditoExcedePago")
      : t("cxp.montoReq");
    if (!form.cuenta_bancaria)                   e.cuenta_bancaria  = t("cxp.cuentaBancariaReq");
    if (!form.documento.trim())                  e.documento        = t("cxp.referenciaObligatoria");
    if (selectedFacturas.size === 0)              e.facturas         = t("cxp.seleccionaFacturaCxP");
    // Un saldo a favor necesita una factura pendiente destino donde aplicarse
    else if (hayNeg && !hayPos)                   e.facturas         = t("cxp.saldoFavorNecesitaDestino");
    if (form.cuenta_bancaria && form.monto) {
      const banco = cuentasBancarias.find(b => b.id === form.cuenta_bancaria);
      if (banco) {
        const bMoneda = (banco.moneda || 'CRC').toUpperCase();
        const tc = Number(form.tipo_cambio) || 1;
        const montoEnBanco = bMoneda === form.moneda_pago
          ? Number(form.monto)
          : bMoneda === 'USD'
            ? Number(form.monto) / tc
            : Number(form.monto) * tc;
        if (montoEnBanco > Number(banco.saldo || 0)) {
          const saldoStr = bMoneda === 'USD' ? `$${fmt(banco.saldo||0)}` : `₡${fmt(banco.saldo||0)}`;
          e.cuenta_bancaria = `${t("cxp.saldoInsuficiente")} — disponible: ${saldoStr}`;
        }
      }
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function handleSubmit(ev) {
    ev.preventDefault();
    setApiErr(""); setSuccessMsg("");
    if (!validate()) return;
    setSaving(true);
    try {
      const facturasToProcess = facturasCxP.filter(f => selectedFacturas.has(f.id));
      if (facturasToProcess.length === 0) {
        setApiErr(t("cxp.seleccionaFacturaCxP"));
        setSaving(false);
        return;
      }

      const isSingle = facturasToProcess.length === 1;
      const hayCruzado = facturasToProcess.some(
        f => (f.moneda || 'CRC') !== form.moneda_pago
      );

      let body;
      if (isSingle) {
        // Caso simple: un solo recibo con factura_cxp directa
        const cxp = facturasToProcess[0];
        body = {
          fecha_pago:              form.fecha_pago,
          monto:                   Number(form.monto),
          metodo_pago:             form.metodo_pago,
          documento:               form.documento.trim(),
          descripcion:             form.descripcion.trim(),
          cuenta_bancaria:         form.cuenta_bancaria,
          factura_cxp:             cxp?.factura_cxp?.id ?? null,
          moneda_pago:             form.moneda_pago,
          tipo_cambio:             Number(form.tipo_cambio) || 1,
          tratar_como_diferencial: hayCruzado && form.tratar_como_diferencial,
        };
      } else {
        // Múltiples facturas → un único ReciboCxP con detalles
        const detalles = facturasToProcess.map(cxp => ({
          factura_cxp:             cxp?.factura_cxp?.id ?? null,
          monto:                   Number(cxp.saldo),
          tratar_como_diferencial: ((cxp.moneda || 'CRC') !== form.moneda_pago) && form.tratar_como_diferencial,
        }));
        body = {
          fecha_pago:      form.fecha_pago,
          monto:           Number(form.monto),
          metodo_pago:     form.metodo_pago,
          documento:       form.documento.trim(),
          descripcion:     form.descripcion.trim(),
          cuenta_bancaria: form.cuenta_bancaria,
          moneda_pago:     form.moneda_pago,
          tipo_cambio:     Number(form.tipo_cambio) || 1,
          detalles,
        };
      }

      const res = await apiFetch("/api/finance/reciboCxP/", {
        method: "POST",
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json();
        const k = Object.keys(err)[0];
        throw new Error(k ? `${k}: ${Array.isArray(err[k]) ? err[k][0] : err[k]}` : "Error al guardar");
      }
      const created = await res.json();

      setSuccessMsg(t("cxp.pagoRegistradoMsg", { numero: created.numero_recibo }));
      if (form.cuenta_proveedor) {
        apiFetch(`/api/finance/cuentaXPagar/?page_size=9999`)
          .then(r => r.json())
          .then(d => {
            const all = d.results ?? [];
            setFacturasCxP(pickFacturas(all, form.cuenta_proveedor));
          });
      }
      setSelectedFacturas(new Set());
      setForm(p => ({ ...p, monto: "", documento: "" }));
      setErrors({});
      setPage(1);
    } catch (ex) { setApiErr(ex.message); }
    finally { setSaving(false); }
  }

  // Totales en colones (para summary y validación bancaria).
  // valorCRC cuenta los saldos a favor en negativo.
  const totalSeleccionado = facturasCxP
    .filter(f => selectedFacturas.has(f.id))
    .reduce((s, f) => s + valorCRC(f), 0);

  const totalPendienteProveedor = facturasCxP
    .reduce((s, f) => s + valorCRC(f), 0);

  const totalPages = Math.ceil(facturasCxP.length / PAGE_SIZE);
  const visibles   = facturasCxP.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const errorBorder = (field) => errors[field] ? { borderColor:"#ef4444" } : undefined;
  const montoNum   = Number(form.monto) || 0;
  const bancoDato  = cuentasBancarias.find(b => b.id === form.cuenta_bancaria);
  const bancoMoneda = (bancoDato?.moneda || 'CRC').toUpperCase();
  // Comparar monto vs saldo bancario en la misma moneda del banco
  const montoParaValidar = bancoMoneda === form.moneda_pago
    ? montoNum
    : bancoMoneda === 'USD'
      ? montoNum / (Number(form.tipo_cambio) || 1)   // CRC pago → USD banco
      : montoNum * (Number(form.tipo_cambio) || 1);  // USD pago → CRC banco
  const saldoInsuficiente = bancoDato && montoNum > 0 && montoParaValidar > Number(bancoDato.saldo || 0);

  return (
    <>
      <span className={styles.inactive}>
        <Link to="/">
          <span className={styles.inactive}>{t("common.inicio")} /</span>
        </Link>
        <Link to="/finanzas/cxp">
          <span className={styles.inactive}> {t("cxp.titulo")} /</span>
        </Link>
        <Link to="/finanzas/cxp/recibos">
          <span className={styles.inactive}> {t("cxp.recibos")} /</span>
        </Link>
        <span className={styles.active}> {t("common.agregar")}</span>
      </span>

      <h2 className="mt-2 me-2 p-0 title">{t("cxp.registrarPagoProveedor")}</h2>

      {/* Éxito */}
      {successMsg && (
        <div style={{ background:"rgba(74,222,128,0.08)", border:"1px solid rgba(74,222,128,0.3)",
          borderLeft:"4px solid #4ade80", borderRadius:8, padding:"12px 18px", marginBottom:16,
          display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:10 }}>
          <span style={{ color:"#4ade80", fontWeight:500, fontSize:"0.9rem" }}>
            <i className="fa-solid fa-circle-check" style={{ marginRight:8 }}/>{successMsg}
          </span>
          <button onClick={() => setSuccessMsg("")}
            style={{ background:"none", border:"none", color:"#4ade80", cursor:"pointer" }}>✕</button>
        </div>
      )}

      {/* Error API */}
      {apiErr && (
        <div style={{ background:"rgba(239,68,68,0.08)", border:"1px solid rgba(239,68,68,0.35)",
          borderLeft:"4px solid #ef4444", borderRadius:8, padding:"12px 18px", marginBottom:16,
          color:"#f87171", fontSize:"0.88rem", display:"flex", alignItems:"center", gap:8 }}>
          <i className="fa-solid fa-circle-exclamation"/>{apiErr}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className={styles["receipt-container"]}>

          {/* ── Izquierda: Detalles del Pago ── */}
          <div className={styles["receipt-details"]}>
            <div className={styles["receipt-details-header"]}>
              <h1>
                <i className={`fa-solid fa-money-bill-transfer ${styles["icon-receipt-details"]}`}/>
                {t("cxp.detallesPago")}
              </h1>
            </div>
            <div className={styles["receipt-details-body"]} style={{ marginTop:14 }}>

              {/* Proveedor */}
              <div>
                <label className="form-label">
                  {t("cxp.proveedor")} <span style={{ color:"#f87171" }}>*</span>
                </label>
                <select className="form-select" value={form.cuenta_proveedor ?? ""}
                  onChange={handleProveedor} style={errorBorder("cuenta_proveedor")}>
                  <option value="">{t("cxp.seleccionaProveedor")}</option>
                  {cuentasProveedor.map(c => (
                    <option key={c.id} value={c.id}>{c.codigo} — {c.nombre}</option>
                  ))}
                </select>
                <FieldError msg={errors.cuenta_proveedor}/>
              </div>

              {/* Fecha + Método */}
              <div className="row">
                <div className="col">
                  <div className="form-group">
                    <label className="form-label">
                      {t("cxp.fechaPago")} <span style={{ color:"#f87171" }}>*</span>
                    </label>
                    <input className="form-control" type="date" value={form.fecha_pago}
                      onChange={e => set("fecha_pago", e.target.value)}
                      style={errorBorder("fecha_pago")}/>
                    <FieldError msg={errors.fecha_pago}/>
                  </div>
                </div>
                <div className="col">
                  <div className="form-group">
                    <label className="form-label">{t("cxp.medioPago")}</label>
                    <select className="form-select" value={form.metodo_pago}
                      onChange={e => set("metodo_pago", e.target.value)}>
                      <option value="TRANSFERENCIA">{t("cxp.transferencia")}</option>
                      <option value="EFECTIVO">{t("cxp.efectivo")}</option>
                      <option value="CHEQUE">{t("cxp.cheque")}</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Monto */}
              <div className="row">
                <div className="form-group">
                  <label className="form-label">
                    {t("cxp.monto") || "Monto"}{" "}
                    <span style={{ color: form.moneda_pago === 'USD' ? '#60a5fa' : '#4ade80',
                      fontWeight: 700, fontSize: '0.82rem' }}>
                      ({form.moneda_pago === 'USD' ? '$ USD' : '₡ CRC'})
                    </span>{" "}
                    <span style={{ color:"#f87171" }}>*</span>
                  </label>
                  <input className="form-control" type="number" min="0.01" step="0.01"
                    placeholder="0.00" value={form.monto}
                    readOnly={selectedFacturas.size > 1}
                    onChange={e => set("monto", e.target.value)}
                    style={{
                      ...errorBorder("monto"),
                      ...(selectedFacturas.size > 1
                        ? { background:"rgba(255,255,255,0.04)", cursor:"not-allowed", color:"#f97316" }
                        : {}),
                    }}/>
                  <FieldError msg={errors.monto}/>
                  {selectedFacturas.size > 1 && (
                    <div style={{ fontSize:"0.73rem", color:"#f97316", marginTop:3 }}>
                      <i className="fa-solid fa-lock" style={{ marginRight:4 }}/>
                      {t("cxp.sumaFijaMsg", { n: selectedFacturas.size })}
                    </div>
                  )}
                </div>
              </div>

              {/* Moneda — solo visible cuando multimoneda está activo */}
              {multimoneda && (
                <MonedaSelector
                  moneda={form.moneda_pago}
                  onMonedaChange={(m) => set("moneda_pago", m)}
                  tipoCambio={form.tipo_cambio}
                  onTCChange={(tc) => set("tipo_cambio", tc)}
                  loadingTC={loadingTC}
                  tcFuente={tipoCambio?.fuente}
                  monto={montoNum}
                  tcLabel={form.moneda_pago === "USD" ? "compra" : "ref"}
                />
              )}

              {/* Aviso + diferencial cuando hay pago cruzado */}
              {selectedFacturas.size > 0 && (() => {
                const sel = facturasCxP.filter(f => selectedFacturas.has(f.id));
                const hasCruzado = sel.some(f => (f.moneda||'CRC') !== form.moneda_pago);
                if (!hasCruzado) return null;
                return (
                  <div style={{ background:"rgba(251,146,60,0.08)",
                    border:"1px solid rgba(251,146,60,0.3)", borderRadius:7,
                    padding:"10px 14px", fontSize:"0.8rem" }}>
                    <div style={{ color:"#fb923c", fontWeight:600, marginBottom:6 }}>
                      <i className="fa-solid fa-arrow-right-arrow-left" style={{ marginRight:6 }}/>
                      {t("cxp.pagoCruzado")}
                    </div>
                    <label style={{ display:"flex", alignItems:"center", gap:8,
                      cursor:"pointer", color:"var(--text-secondary)" }}>
                      <input type="checkbox"
                        checked={form.tratar_como_diferencial}
                        onChange={e => set("tratar_como_diferencial", e.target.checked)}
                        style={{ accentColor:"#f97316", width:14, height:14 }}/>
                      <span style={{ fontSize:"0.78rem" }}>
                        {t("cxp.tratarDiferencial")}
                      </span>
                    </label>
                  </div>
                );
              })()}

              {/* Cuenta bancaria */}
              <div className="row">
                <div className="col">
                  <div className="form-group">
                    <label className="form-label">
                      {t("cxp.cuentaBancaria")} <span style={{ color:"#f87171" }}>*</span>
                    </label>
                    {cuentasBancarias.length === 0 ? (
                      <div style={{ background:"rgba(239,68,68,0.08)", border:"1px solid rgba(239,68,68,0.3)",
                        borderRadius:6, padding:"8px 12px", color:"#f87171", fontSize:"0.82rem",
                        display:"flex", alignItems:"center", gap:7 }}>
                        <i className="fa-solid fa-triangle-exclamation"/>
                        {t("cxp.sinCuentasBancariasSettings")}
                      </div>
                    ) : (
                      <>
                        <select className="form-select"
                          value={form.cuenta_bancaria ?? ""}
                          onChange={handleBancoChange}
                          style={errorBorder("cuenta_bancaria")}>
                          {cuentasBancarias.map(cb => {
                            const cbMoneda = (cb.moneda || 'CRC').toUpperCase();
                            const saldoStr = cbMoneda === 'USD' ? `$${fmt(cb.saldo||0)}` : `₡${fmt(cb.saldo||0)}`;
                            return (
                              <option key={cb.id} value={cb.id}>
                                {cb.banco} — {cb.numero_cuenta}{cbMoneda === 'USD' ? ' (USD)' : ''} — {t("cxp.saldo")}: {saldoStr}
                              </option>
                            );
                          })}
                        </select>
                        <FieldError msg={errors.cuenta_bancaria}/>
                        {saldoInsuficiente && (
                          <div style={{ marginTop:4, padding:"5px 10px",
                            background:"rgba(239,68,68,0.08)", border:"1px solid rgba(239,68,68,0.3)",
                            borderRadius:6, color:"#f87171", fontSize:"0.8rem",
                            display:"flex", alignItems:"center", gap:6 }}>
                            <i className="fa-solid fa-triangle-exclamation"/>
                            {t("cxp.saldoInsuficiente")} — disponible: {bancoMoneda === 'USD' ? `$${fmt(bancoDato?.saldo||0)}` : `₡${fmt(bancoDato?.saldo||0)}`}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Referencia */}
              <div className="row">
                <div className="form-group">
                  <label className="form-label">
                    {t("cxp.referenciaBancaria")} <span style={{ color:"#f87171" }}>*</span>
                  </label>
                  <input className="form-control" type="text"
                    placeholder={t("cxp.referenciaPlaceholder")}
                    value={form.documento}
                    onChange={e => set("documento", e.target.value)}
                    style={errorBorder("documento")}/>
                  <FieldError msg={errors.documento}/>
                </div>
              </div>

              {/* Descripción (opcional) */}
              <div className="row">
                <div className="form-group">
                  <label className="form-label">
                    {t("cxp.descripcionLabel")}{" "}
                    <span style={{ color:"var(--text-muted)", fontWeight:400 }}>
                      ({t("common.opcional")})
                    </span>
                  </label>
                  <input className="form-control" type="text"
                    placeholder={t("cxp.descripcionPlaceholder")}
                    value={form.descripcion}
                    onChange={e => set("descripcion", e.target.value)}/>
                  <div style={{ fontSize:"0.73rem", color:"var(--text-muted)", marginTop:3 }}>
                    {t("cxp.descripcionHint")}
                  </div>
                </div>
              </div>

            </div>
          </div>

          {/* ── Derecha: Facturas Pendientes ── */}
          <div className={styles["cxc-details"]}>
            <div className={styles["cxc-details-header"]}>
              <h1>
                <i className={`fa-solid fa-file-invoice ${styles["icon-cxc-details"]}`}/>
                {t("cxp.facturasPendientes")}
              </h1>
            </div>

            <div className={styles["receipt-details-body"]}>
              {!form.cuenta_proveedor ? (
                <div style={{ padding:"40px 16px", textAlign:"center", color:"var(--text-muted)" }}>
                  <i className="fa-solid fa-building" style={{ fontSize:"2rem", display:"block",
                    marginBottom:12, color:"var(--text-muted)" }}/>
                  {t("cxp.sinProveedor")}
                </div>
              ) : loadingFac ? (
                <div style={{ padding:40, textAlign:"center", color:"var(--text-secondary)" }}>
                  <i className="fa-solid fa-circle-notch fa-spin"
                     style={{ color:"#f97316", fontSize:"1.3rem" }}/>
                </div>
              ) : facturasCxP.length === 0 ? (
                <div style={{ padding:32, textAlign:"center", color:"var(--text-muted)", fontSize:"0.87rem" }}>
                  <i className="fa-solid fa-check-circle" style={{ color:"#4ade80",
                    fontSize:"1.4rem", display:"block", marginBottom:8 }}/>
                  {t("cxp.sinFacturasCxP")}
                </div>
              ) : (
                <>
                  {/* Tabla de facturas */}
                  <div style={{ width:"100%", overflowX:"auto",
                    border:"1px solid var(--border-card)", borderRadius:8 }}>
                    <table style={{ width:"100%", borderCollapse:"collapse",
                      color:"var(--text-primary)", minWidth:0 }}>
                      <thead>
                        <tr style={{ background:"var(--bg-card)",
                          borderBottom:"1px solid var(--border-card)" }}>
                          <th style={{ width:36, padding:"10px 12px",
                            textAlign:"center", color:"var(--text-secondary)",
                            fontSize:"0.8rem", fontWeight:500 }}>
                            <input type="checkbox"
                              checked={selectedFacturas.size === facturasCxP.length}
                              onChange={toggleAll}
                              style={{ accentColor:"#f97316" }}/>
                          </th>
                          <th style={{ padding:"10px 12px", textAlign:"left",
                            color:"var(--text-secondary)", fontSize:"0.8rem", fontWeight:500,
                            width:"30%" }}>{t("cxp.numFactura")}</th>
                          <th style={{ padding:"10px 12px", textAlign:"left",
                            color:"var(--text-secondary)", fontSize:"0.8rem", fontWeight:500 }}>
                            {t("cxp.concepto")}
                          </th>
                          <th style={{ padding:"10px 12px", textAlign:"center",
                            color:"var(--text-secondary)", fontSize:"0.8rem", fontWeight:500,
                            width:"18%" }}>{t("common.moneda")}</th>
                          <th style={{ padding:"10px 12px", textAlign:"right",
                            color:"var(--text-secondary)", fontSize:"0.8rem", fontWeight:500,
                            width:"22%" }}>{t("cxp.saldo")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {visibles.map(cxp => {
                          const fac = cxp.factura_cxp;
                          const sel = selectedFacturas.has(cxp.id);
                          const esFavor = Number(cxp.saldo) < 0;
                          return (
                            <tr key={cxp.id} style={{
                              background: sel ? "rgba(249,115,22,0.08)" : "transparent",
                              cursor: "pointer",
                              borderBottom: "1px solid var(--border-card)",
                            }} onClick={() => toggleFactura(cxp.id)}>
                              <td style={{ width:36, padding:"10px 12px", textAlign:"center" }}>
                                <input type="checkbox" checked={sel}
                                  onChange={() => toggleFactura(cxp.id)}
                                  onClick={e => e.stopPropagation()}
                                  style={{ accentColor:"#f97316" }}/>
                              </td>
                              <td style={{ padding:"10px 12px", fontFamily:"monospace",
                                color:"#f97316", fontWeight:600, fontSize:"0.83rem" }}>
                                {fac?.numero_doc_proveedor || fac?.numero_factura || "—"}
                                {esFavor && (
                                  <span style={{ marginLeft:6, padding:"1px 6px", borderRadius:4,
                                    fontSize:"0.65rem", fontWeight:700,
                                    background:"rgba(74,222,128,0.15)", color:"#4ade80" }}>
                                    {t("cxp.aFavor")}
                                  </span>
                                )}
                              </td>
                              <td style={{ padding:"10px 12px", color:"var(--text-secondary)",
                                fontSize:"0.8rem", overflow:"hidden",
                                textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                                {cxp.concepto || fac?.concepto || "—"}
                              </td>
                              <td style={{ padding:"10px 12px", textAlign:"center" }}>
                                <span style={{
                                  display:"inline-block", padding:"2px 7px",
                                  borderRadius:4, fontSize:"0.72rem", fontWeight:700,
                                  background: (cxp.moneda||'CRC')==='USD'
                                    ? "rgba(96,165,250,0.15)" : "rgba(74,222,128,0.12)",
                                  color: (cxp.moneda||'CRC')==='USD' ? "#60a5fa" : "#4ade80",
                                  border: `1px solid ${(cxp.moneda||'CRC')==='USD'
                                    ? "rgba(96,165,250,0.35)" : "rgba(74,222,128,0.3)"}`,
                                }}>
                                  {cxp.moneda||'CRC'}
                                </span>
                              </td>
                              <td style={{ padding:"10px 12px", textAlign:"right",
                                fontFamily:"monospace", color: esFavor ? "#4ade80" : "#facc15", fontWeight:600 }}>
                                {(cxp.moneda||'CRC')==='USD'
                                  ? `$ ${fmt(cxp.saldo)}`
                                  : `₡${fmt(cxp.saldo)}`}
                                {(cxp.moneda||'CRC')==='USD' && (
                                  <div style={{ fontSize:"0.68rem", color:"var(--text-muted)",
                                    fontWeight:400, marginTop:1 }}>
                                    ≈ ₡{fmt(cxp.saldo_colones||0)}
                                  </div>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    {/* Footer paginación */}
                    <div style={{ width:"100%", textAlign:"center",
                      padding:"8px 0", fontSize:13, color:"var(--text-secondary)",
                      background:"var(--bg-card)", borderTop:"1px solid var(--border-card)",
                      boxSizing:"border-box" }}>
                      {facturasCxP.length} {t("cxp.facturasPendientesSufijo")} ·{" "}
                      {t("common.pagina")} {page} {t("common.de")} {Math.max(1, totalPages)}
                      {totalPages > 1 && (
                        <span style={{ marginLeft:16 }}>
                          <button type="button" disabled={page <= 1}
                            onClick={() => setPage(p => p - 1)}
                            style={{ background:"none", border:"1px solid var(--border-card)",
                              borderRadius:4, color:"var(--text-secondary)", cursor:"pointer",
                              padding:"2px 8px", marginRight:6 }}>←</button>
                          <button type="button" disabled={page >= totalPages}
                            onClick={() => setPage(p => p + 1)}
                            style={{ background:"none", border:"1px solid var(--border-card)",
                              borderRadius:4, color:"var(--text-secondary)", cursor:"pointer",
                              padding:"2px 8px" }}>→</button>
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Resumen */}
                  <div className={styles["cxc-details-summary"]}>
                    <div className={styles["cxc-summary-relevant"]}>
                      <h2>{t("cxp.resumenProveedor")}</h2>
                      <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                        <span>{t("cxp.totalPendienteProveedor")}</span>
                        <span style={{ color:"#f87171", fontWeight:700 }}>₡{fmt(totalPendienteProveedor)}</span>
                      </div>
                      {selectedFacturas.size > 0 && (
                        <div style={{ display:"flex", justifyContent:"space-between" }}>
                          <span>{t("cxp.seleccionadoCount")} ({selectedFacturas.size})</span>
                          <span style={{ color:"#f97316", fontWeight:700 }}>₡{fmt(totalSeleccionado)}</span>
                        </div>
                      )}
                    </div>
                    <div className={styles["cxc-summary-important"]}>
                      <h2>{t("cxp.totalAPagar")}</h2>
                      <span>
                        {form.moneda_pago === 'USD'
                          ? `$${montoNum.toFixed(2)}`
                          : `₡${fmt(montoNum)}`}
                      </span>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* ── Footer ── */}
          <div className={styles["receipt-footer"]}>
            <div className={styles.actions}>
              <Link to="/finanzas/cxp/recibos"
                className={`${styles.btn} ${styles["btn-clean-form"]}`}
                style={{ textDecoration:"none" }}>
                {t("common.cancelar")}
              </Link>
              <button type="submit" disabled={saving}
                className={`${styles.btn} ${styles["btn-save"]}`}
                style={{ opacity: saving ? 0.7 : 1 }}>
                {saving
                  ? <><i className="fa-solid fa-circle-notch fa-spin"/> {t("cxp.guardando")}</>
                  : <><i className="fa-solid fa-lock"/> {t("cxp.registrarPagoBtn")}</>}
              </button>
            </div>
          </div>

        </div>
      </form>
    </>
  );
}
